import { useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { Location } from '../../types'

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

// Custom icons with amber/treasure theme
const userIcon = new L.DivIcon({
  className: 'user-marker',
  html: `<div style="width: 28px; height: 28px; background: linear-gradient(135deg, #f59e0b, #d97706); border: 3px solid white; border-radius: 50%; box-shadow: 0 3px 8px rgba(217, 119, 6, 0.4); display: flex; align-items: center; justify-content: center;">
    <svg width="14" height="14" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.9391 9.04932L16.8353 15.3596C16.7208 15.7032 16.5278 16.0153 16.2718 16.2714C16.0157 16.5275 15.7036 16.7204 15.36 16.8349L9.04971 18.9387L11.1535 12.6284C11.268 12.2848 11.461 11.9727 11.717 11.7166C11.9731 11.4606 12.2852 11.2676 12.6288 11.1531L18.9391 9.04932Z" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
})

const waypointIcon = new L.DivIcon({
  className: 'waypoint-marker',
  html: `<div style="position: relative;">
    <div style="width: 36px; height: 36px; background: linear-gradient(135deg, #ef4444, #dc2626); border: 3px solid white; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); box-shadow: 0 3px 8px rgba(220, 38, 38, 0.4); display: flex; align-items: center; justify-content: center;">
      <span style="transform: rotate(45deg); font-size: 16px;">📍</span>
    </div>
    <div style="position: absolute; top: 0; left: 0; width: 36px; height: 36px; background: rgba(239, 68, 68, 0.3); border-radius: 50%; animation: pulse 2s ease-in-out infinite;"></div>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
})

const completedIcon = new L.DivIcon({
  className: 'completed-marker',
  html: `<div style="width: 32px; height: 32px; background: linear-gradient(135deg, #22c55e, #16a34a); border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 6px rgba(22, 163, 74, 0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 14px;">✓</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
})

interface MapUpdaterProps {
  center: [number, number]
  zoom?: number
}

function MapUpdater({ center, zoom = 15 }: MapUpdaterProps) {
  const map = useMap()

  useEffect(() => {
    map.setView(center, zoom)
  }, [map, center, zoom])

  return null
}

// Fix for scattered tiles issue - invalidate size after mount
function MapResizer() {
  const map = useMap()

  useEffect(() => {
    // Small delay to ensure container is fully rendered
    const timer = setTimeout(() => {
      map.invalidateSize()
    }, 100)

    // Also invalidate on window resize
    const handleResize = () => map.invalidateSize()
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
    }
  }, [map])

  return null
}

interface HuntMapProps {
  userLocation?: Location | null
  waypointLocation?: { lat: number; lng: number }
  waypointRadius?: number
  waypointCompleted?: boolean
  className?: string
}

export default function HuntMap({
  userLocation,
  waypointLocation,
  waypointRadius = 50,
  waypointCompleted = false,
  className = '',
}: HuntMapProps) {
  // Default center (will be overridden by user location)
  const defaultCenter: [number, number] = userLocation
    ? [userLocation.latitude, userLocation.longitude]
    : waypointLocation
    ? [waypointLocation.lat, waypointLocation.lng]
    : [51.505, -0.09]

  return (
    <div className={`${className}`} style={{ position: 'relative', overflow: 'hidden' }}>
      <MapContainer
        center={defaultCenter}
        zoom={16}
        style={{ width: '100%', height: '100%', borderRadius: '0.75rem', overflow: 'hidden' }}
        zoomControl={false}
        attributionControl={false}
      >
        <MapResizer />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />

        {userLocation && (
          <>
            <Marker
              position={[userLocation.latitude, userLocation.longitude]}
              icon={userIcon}
            >
              <Popup>You are here</Popup>
            </Marker>
            <MapUpdater center={[userLocation.latitude, userLocation.longitude]} />
          </>
        )}

        {waypointLocation && (
          <>
            <Marker
              position={[waypointLocation.lat, waypointLocation.lng]}
              icon={waypointCompleted ? completedIcon : waypointIcon}
            >
              <Popup>{waypointCompleted ? 'Completed!' : 'Your target'}</Popup>
            </Marker>

            {!waypointCompleted && (
              <Circle
                center={[waypointLocation.lat, waypointLocation.lng]}
                radius={waypointRadius}
                pathOptions={{
                  color: '#d97706',
                  fillColor: '#f59e0b',
                  fillOpacity: 0.15,
                  weight: 2,
                }}
              />
            )}
          </>
        )}
      </MapContainer>

      {/* Attribution */}
      <div className="absolute bottom-1 right-1 text-[10px] text-brown-500 bg-cream-50/90 px-1.5 py-0.5 rounded">
        OpenStreetMap
      </div>
    </div>
  )
}
